/**
 * Replaces media IDs in HTML img src attributes with presigned URLs.
 *
 * The API stores images in HTML content as `<img src="MEDIA_ID">`.
 * The response also includes a `mediaResponses` map that provides
 * presigned S3 URLs for each media ID. This function performs the
 * substitution so images render correctly in the browser.
 *
 * Used by task detail page (description), auditor observation, and
 * any other field that contains HTML with embedded media references.
 */

interface MediaEntry {
  media: { mediaId: string };
  presignedUrl: string;
}

/**
 * Replaces media ID references in HTML content with their presigned URLs.
 *
 * @param content - HTML string containing `<img src="mediaId">` references
 * @param mediaResponses - Map of field categories to media entry arrays
 * @returns HTML with media IDs replaced by presigned URLs
 */
export function replaceMediaIdsWithUrls(
  content: string,
  mediaResponses: Record<string, MediaEntry[]> | null | undefined,
): string {
  if (!content || !mediaResponses) return content || '';

  let result = content;

  for (const category of Object.keys(mediaResponses)) {
    // eslint-disable-next-line security/detect-object-injection -- iterating own keys from API response
    const entries = mediaResponses[category];
    if (!entries) continue;

    for (const entry of entries) {
      if (!entry.media?.mediaId || !entry.presignedUrl) continue;

      // Replace src attribute that is exactly this media ID (bare ID as full src value).
      // eslint-disable-next-line security/detect-non-literal-regexp -- media IDs are sanitized via escapeRegex
      const pattern = new RegExp(`src="${escapeRegex(entry.media.mediaId)}"`, 'g');
      // Use function replacement to avoid $ special character interpretation in presigned URLs
      result = result.replace(pattern, () => `src="${entry.presignedUrl}"`);
    }
  }

  // Force responsive images by stripping inline width/height and adding
  // inline max-width style. This ensures images fit within the viewport
  // regardless of Emotion CSS specificity or inline attribute conflicts.
  result = result.replace(/<img\b([^>]*)>/gi, (_match, attrs: string) => {
    const cleaned = attrs
      .replace(/\s*width="[^"]*"/gi, '')
      .replace(/\s*height="[^"]*"/gi, '')
      .replace(/\s*style="[^"]*"/gi, '');
    return `<img${cleaned} style="max-width:100%;height:auto">`;
  });

  return result;
}

/** Escapes special regex characters in a string. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
